import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  formatDate,
  formatLocaleNumber,
  formatMoneyCompact,
  formatMoneyWithSeparators,
  formatPercentValue,
} from '@/utils';
import { DateFormats } from '@/constants/dates';
import { COLORS } from '@/theme';
import { Table } from '@/ui';
import {
  IAdminMainMetricsData,
  IAdminMainMetricsKpis,
  IAdminRecords,
  ICatalogBlock,
  IEngagementBlock,
  INamedShareItem,
  IPaidBlock,
  IPaymentTypeItem,
  IStockBlock,
  IStockSupplierRow,
  ISuppliersBlock,
  ITopProductRow,
  ITopSupplierRow,
  TopOrderType,
  TopSortKey,
} from '@/models/dashboard.model';
import { downloadCsv } from '../../helpers';
import DynamicsCell from '../DynamicsCell/DynamicsCell';
import ExportCsvButton from '../ExportCsvButton/ExportCsvButton';
import DualLineChart from '../DualLineChart/DualLineChart';
import {
  MAIN_METRICS_CHART_MARGIN,
  MAIN_METRICS_Y_AXIS_WIDTH,
  StyledChartBox,
  StyledChartWithAside,
  StyledDonutCenter,
  StyledDonutWrap,
  StyledDot,
  StyledLegend,
  StyledLegendItem,
  StyledMetricCard,
  StyledMetricContext,
  StyledMetricGrid,
  StyledMetricGrid3,
  StyledMetricGrid4,
  StyledMetricLabel,
  StyledMetricLabelRow,
  StyledMetricValue,
  StyledPanel,
  StyledPanelNote,
  StyledPanelTitle,
  StyledPagerButton,
  StyledPagerLabel,
  StyledRecordsMetrics,
  StyledRecordsPanel,
  StyledTablePager,
  StyledToggleButton,
  StyledToggleGroup,
  StyledToolbar,
} from '../MainMetricsSharedStyles';
import InfoLabel from '../InfoLabel';

const DONUT_COLORS = [COLORS.BLUE, COLORS.YELLOW_PRIMARY, COLORS.GREEN, COLORS.ORANGE, COLORS.PURPLE];
const TOP_TABLE_PER_PAGE = 10;

interface IDonutLegendItem {
  key: string;
  name: string;
  amount: number;
  share_percent: number;
}

const RADIAN = Math.PI / 180;

const renderDonutLabel = (
  props: { cx: number; cy: number; midAngle: number; outerRadius: number; share_percent: number },
  language: string,
) => {
  const { cx, cy, midAngle, outerRadius } = props;
  const sharePercent = props.share_percent;
  const radius = outerRadius + 24;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={500} fill={COLORS.BLACK}>
      {formatPercentValue(sharePercent, 1, language)}
    </text>
  );
};

const MoneyDonut = ({ title, tooltip, items }: { title: string; tooltip: string; items: IDonutLegendItem[] }) => {
  const { t, i18n } = useTranslation(['dashboard']);
  const total = items.reduce((sum, item) => sum + item.amount, 0);

  return (
    <div>
      <StyledPanelTitle>
        <InfoLabel label={title} tooltip={tooltip} />
      </StyledPanelTitle>
      <StyledDonutWrap>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={items}
              dataKey="amount"
              nameKey="name"
              innerRadius={80}
              outerRadius={105}
              paddingAngle={2}
              label={(props) => renderDonutLabel(props, i18n.language)}
              labelLine={false}
              isAnimationActive={false}
            >
              {items.map((item, index) => (
                <Cell key={item.key} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number) => `${formatMoneyWithSeparators(value)} ${t('dashboard_grn')}`} />
          </PieChart>
        </ResponsiveContainer>
        <StyledDonutCenter>{`${formatMoneyCompact(total, i18n.language)} ${t('dashboard_grn')}`}</StyledDonutCenter>
      </StyledDonutWrap>
      <StyledLegend>
        {items.map((item, index) => (
          <StyledLegendItem key={item.key}>
            <StyledDot $color={DONUT_COLORS[index % DONUT_COLORS.length]} />
            {`${item.name}: ${formatMoneyWithSeparators(item.amount)} ${t('dashboard_grn')} · ${formatPercentValue(
              item.share_percent,
              1,
              i18n.language,
            )}`}
          </StyledLegendItem>
        ))}
      </StyledLegend>
    </div>
  );
};

const toPaymentDonutItems = (items: IPaymentTypeItem[]): IDonutLegendItem[] =>
  items.map((item) => ({
    key: item.code,
    name: item.label,
    amount: item.amount,
    share_percent: item.share_percent,
  }));

const toRegionDonutItems = (items: INamedShareItem[]): IDonutLegendItem[] =>
  items.map((item) => ({
    key: item.id,
    name: item.name,
    amount: item.amount,
    share_percent: item.share_percent,
  }));

const TablePager = ({ page, pages, onChange }: { page: number; pages: number; onChange: (page: number) => void }) => {
  if (pages <= 1) return null;

  return (
    <StyledTablePager>
      <StyledPagerButton type="button" disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label="prev">
        <LeftOutlined />
      </StyledPagerButton>
      <StyledPagerLabel>{`${page} / ${pages}`}</StyledPagerLabel>
      <StyledPagerButton type="button" disabled={page >= pages} onClick={() => onChange(page + 1)} aria-label="next">
        <RightOutlined />
      </StyledPagerButton>
    </StyledTablePager>
  );
};

export const RecordsBanner = ({ records }: { records: IAdminRecords }) => {
  const { t } = useTranslation(['dashboard']);
  return (
    <StyledRecordsPanel>
      <StyledPanelTitle>{t('main_metrics_records_title')}</StyledPanelTitle>
      <StyledRecordsMetrics>
        <div>
          <StyledMetricLabel>
            <InfoLabel label={t('main_metrics_records_orders')} tooltip={t('main_metrics_records_orders_tooltip')} />
          </StyledMetricLabel>
          <StyledMetricValue>{formatLocaleNumber(records.max_active_orders_per_day.value, 0)}</StyledMetricValue>
          <StyledMetricContext>
            {formatDate(records.max_active_orders_per_day.date, DateFormats.DATE_WITHOUT_HOURS)}
          </StyledMetricContext>
        </div>
        <div>
          <StyledMetricLabel>
            <InfoLabel label={t('main_metrics_records_amount')} tooltip={t('main_metrics_records_amount_tooltip')} />
          </StyledMetricLabel>
          <StyledMetricValue>
            {`${formatMoneyWithSeparators(records.max_active_orders_amount_per_day.value)} ${t('dashboard_grn')}`}
          </StyledMetricValue>
          <StyledMetricContext>
            {formatDate(records.max_active_orders_amount_per_day.date, DateFormats.DATE_WITHOUT_HOURS)}
          </StyledMetricContext>
        </div>
      </StyledRecordsMetrics>
    </StyledRecordsPanel>
  );
};

export const MainMetricsKpis = ({ kpis }: { kpis: IAdminMainMetricsKpis }) => {
  const { t, i18n } = useTranslation(['dashboard']);
  return (
    <StyledPanel>
      <StyledPanelTitle>{t('main_metrics_kpis_title')}</StyledPanelTitle>
      <StyledMetricGrid>
        <StyledMetricCard>
          <StyledMetricLabelRow>
            <StyledMetricLabel>
              <InfoLabel
                label={t('main_metrics_kpi_active_count')}
                tooltip={t('main_metrics_kpi_active_count_tooltip')}
              />
            </StyledMetricLabel>
            <DynamicsCell value={kpis.active_orders_count.change_percent} />
          </StyledMetricLabelRow>
          <StyledMetricValue>{formatLocaleNumber(kpis.active_orders_count.value, 0, i18n.language)}</StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabelRow>
            <StyledMetricLabel>
              <InfoLabel
                label={t('main_metrics_kpi_active_amount')}
                tooltip={t('main_metrics_kpi_active_amount_tooltip')}
              />
            </StyledMetricLabel>
            <DynamicsCell value={kpis.active_orders_amount.change_percent} />
          </StyledMetricLabelRow>
          <StyledMetricValue>
            {`${formatMoneyWithSeparators(kpis.active_orders_amount.value)} ${t('dashboard_grn')}`}
          </StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabelRow>
            <StyledMetricLabel>
              <InfoLabel label={t('main_metrics_kpi_avg_check')} tooltip={t('main_metrics_kpi_avg_check_tooltip')} />
            </StyledMetricLabel>
            <DynamicsCell value={kpis.avg_check.change_percent} />
          </StyledMetricLabelRow>
          <StyledMetricValue>
            {`${formatMoneyWithSeparators(kpis.avg_check.value)} ${t('dashboard_grn')}`}
          </StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabel>
            <InfoLabel label={t('main_metrics_kpi_cancelled')} tooltip={t('main_metrics_kpi_cancelled_tooltip')} />
          </StyledMetricLabel>
          <StyledMetricValue>
            {`${formatLocaleNumber(kpis.cancelled.count, 0, i18n.language)} ${t('main_metrics_pcs')}`}
          </StyledMetricValue>
          <StyledMetricContext>
            {`${formatPercentValue(kpis.cancelled.share_percent, 1, i18n.language)} · ${formatMoneyWithSeparators(
              kpis.cancelled.amount,
            )} ${t('dashboard_grn')}`}
          </StyledMetricContext>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabelRow>
            <StyledMetricLabel>
              <InfoLabel label={t('main_metrics_kpi_new_users')} tooltip={t('main_metrics_kpi_new_users_tooltip')} />
            </StyledMetricLabel>
            <DynamicsCell value={kpis.new_users.change_percent} />
          </StyledMetricLabelRow>
          <StyledMetricValue>{formatLocaleNumber(kpis.new_users.value, 0, i18n.language)}</StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabelRow>
            <StyledMetricLabel>
              <InfoLabel label={t('main_metrics_kpi_pr')} tooltip={t('main_metrics_kpi_pr_tooltip')} />
            </StyledMetricLabel>
            <DynamicsCell value={kpis.purchase_rate_pr.change_pp} suffix={t('cancelled_orders_pp_suffix')} />
          </StyledMetricLabelRow>
          <StyledMetricValue>{formatPercentValue(kpis.purchase_rate_pr.value, 1, i18n.language)}</StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabelRow>
            <StyledMetricLabel>
              <InfoLabel label={t('main_metrics_kpi_ltv')} tooltip={t('main_metrics_kpi_ltv_tooltip')} />
            </StyledMetricLabel>
            <DynamicsCell value={kpis.customer_ltv.change_percent} />
          </StyledMetricLabelRow>
          <StyledMetricValue>
            {`${formatMoneyWithSeparators(kpis.customer_ltv.value)} ${t('dashboard_grn')}`}
          </StyledMetricValue>
        </StyledMetricCard>
      </StyledMetricGrid>
    </StyledPanel>
  );
};

export const PaidOrdersSection = ({ paid, groupBy }: { paid: IPaidBlock; groupBy: 'day' | 'week' }) => {
  const { t, i18n } = useTranslation(['dashboard']);

  return (
    <StyledPanel>
      <StyledPanelTitle>{t('main_metrics_paid_title')}</StyledPanelTitle>
      <StyledMetricGrid style={{ marginBottom: '1rem' }}>
        <StyledMetricCard>
          <StyledMetricLabelRow>
            <StyledMetricLabel>
              <InfoLabel label={t('main_metrics_paid_amount')} tooltip={t('main_metrics_paid_amount_tooltip')} />
            </StyledMetricLabel>
            <DynamicsCell value={paid.amount.change_percent} />
          </StyledMetricLabelRow>
          <StyledMetricValue>
            {`${formatMoneyWithSeparators(paid.amount.value)} ${t('dashboard_grn')}`}
          </StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabelRow>
            <StyledMetricLabel>
              <InfoLabel label={t('main_metrics_paid_count')} tooltip={t('main_metrics_paid_count_tooltip')} />
            </StyledMetricLabel>
            <DynamicsCell value={paid.count.change_percent} />
          </StyledMetricLabelRow>
          <StyledMetricValue>{formatLocaleNumber(paid.count.value, 0, i18n.language)}</StyledMetricValue>
        </StyledMetricCard>
      </StyledMetricGrid>
      <StyledChartWithAside>
        <DualLineChart
          title={t('main_metrics_paid_dynamics')}
          tooltip={t('main_metrics_paid_dynamics_tooltip')}
          series={paid.series}
          initialGroupBy={groupBy}
        />
        <MoneyDonut
          title={t('main_metrics_payment_types')}
          tooltip={t('main_metrics_payment_types_tooltip')}
          items={toPaymentDonutItems(paid.payment_types)}
        />
      </StyledChartWithAside>
    </StyledPanel>
  );
};

export const StockSection = ({ stock, groupBy }: { stock: IStockBlock; groupBy: 'day' | 'week' }) => {
  const { t, i18n } = useTranslation(['dashboard']);
  const [showTop100, setShowTop100] = useState(false);
  const rows = stock.top_suppliers.slice(0, showTop100 ? 100 : 10);

  return (
    <StyledPanel>
      <StyledPanelTitle>{t('main_metrics_stock_title')}</StyledPanelTitle>
      <StyledMetricContext>
        {`${t('main_metrics_stock_as_of')}: ${formatDate(stock.as_of, DateFormats.DATE_WITHOUT_HOURS)}`}
      </StyledMetricContext>
      <StyledMetricGrid style={{ margin: '0.75rem 0' }}>
        <StyledMetricCard>
          <StyledMetricLabel>
            <InfoLabel label={t('main_metrics_stock_total')} tooltip={t('main_metrics_stock_total_tooltip')} />
          </StyledMetricLabel>
          <StyledMetricValue>
            {`${formatMoneyWithSeparators(stock.total_amount)} ${t('dashboard_grn')}`}
          </StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabel>
            <InfoLabel label={t('main_metrics_stock_growth')} tooltip={t('main_metrics_stock_growth_tooltip')} />
          </StyledMetricLabel>
          <StyledMetricValue>
            {`${formatMoneyWithSeparators(stock.growth_amount)} ${t('dashboard_grn')}`}
          </StyledMetricValue>
        </StyledMetricCard>
      </StyledMetricGrid>
      <StyledChartWithAside>
        <DualLineChart
          title={t('main_metrics_stock_dynamics')}
          tooltip={t('main_metrics_stock_dynamics_tooltip')}
          series={stock.series}
          initialGroupBy={groupBy}
          showModeToggle={false}
        />
        <MoneyDonut
          title={t('main_metrics_stock_by_region')}
          tooltip={t('main_metrics_stock_by_region_tooltip')}
          items={toRegionDonutItems(stock.by_region)}
        />
      </StyledChartWithAside>
      <StyledPanelNote>{t('main_metrics_stock_note')}</StyledPanelNote>
      <StyledToolbar style={{ marginTop: '1rem' }}>
        <StyledPanelTitle>
          <InfoLabel
            label={t('main_metrics_stock_top_suppliers')}
            tooltip={t('main_metrics_stock_top_suppliers_tooltip')}
          />
        </StyledPanelTitle>
        <StyledToggleGroup>
          <StyledToggleButton type="button" onClick={() => setShowTop100((prev) => !prev)}>
            {showTop100 ? t('main_metrics_show_top_10') : t('main_metrics_show_top_100')}
          </StyledToggleButton>
        </StyledToggleGroup>
      </StyledToolbar>
      <Table<IStockSupplierRow>
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={rows}
        columns={[
          { title: '#', key: 'rank', dataIndex: 'rank', width: 32, align: 'center' as const },
          { title: t('main_metrics_supplier'), key: 'name', dataIndex: 'name' },
          { title: t('main_metrics_amount'), key: 'amount', render: (_, row) => formatMoneyWithSeparators(row.amount) },
          {
            title: t('main_metrics_share'),
            key: 'share',
            render: (_, row) => formatPercentValue(row.share_percent, 1, i18n.language),
          },
          {
            title: t('main_metrics_dynamics'),
            key: 'dynamics',
            render: (_, row) => <DynamicsCell value={row.change_percent} />,
          },
        ]}
      />
    </StyledPanel>
  );
};

export const CatalogSection = ({ catalog }: { catalog: ICatalogBlock }) => {
  const { t, i18n } = useTranslation(['dashboard']);
  return (
    <StyledPanel>
      <StyledPanelTitle>{t('main_metrics_catalog_title')}</StyledPanelTitle>
      <StyledMetricGrid4>
        <StyledMetricCard>
          <StyledMetricLabel>
            <InfoLabel label={t('main_metrics_sku_total')} tooltip={t('main_metrics_sku_total_tooltip')} />
          </StyledMetricLabel>
          <StyledMetricValue>{formatLocaleNumber(catalog.sku_total, 0, i18n.language)}</StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabel>
            <InfoLabel label={t('main_metrics_published')} tooltip={t('main_metrics_published_tooltip')} />
          </StyledMetricLabel>
          <StyledMetricValue>{formatLocaleNumber(catalog.published, 0, i18n.language)}</StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabel>
            <InfoLabel label={t('main_metrics_hidden')} tooltip={t('main_metrics_hidden_tooltip')} />
          </StyledMetricLabel>
          <StyledMetricValue>{formatLocaleNumber(catalog.hidden, 0, i18n.language)}</StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabel>
            <InfoLabel label={t('main_metrics_deleted')} tooltip={t('main_metrics_deleted_tooltip')} />
          </StyledMetricLabel>
          <StyledMetricValue>{formatLocaleNumber(catalog.deleted, 0, i18n.language)}</StyledMetricValue>
        </StyledMetricCard>
      </StyledMetricGrid4>
      <StyledPanelTitle style={{ marginTop: '1rem' }}>
        <InfoLabel label={t('main_metrics_catalog_chart_title')} tooltip={t('main_metrics_catalog_chart_tooltip')} />
      </StyledPanelTitle>
      <StyledChartBox style={{ marginTop: '1rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={catalog.series} margin={MAIN_METRICS_CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.BRIGHT_GRAY} />
            <XAxis
              dataKey="date"
              tickFormatter={(value: string) => formatDate(value, DateFormats.DATE_WITHOUT_HOURS)}
              minTickGap={24}
            />
            <YAxis
              width={MAIN_METRICS_Y_AXIS_WIDTH}
              tickFormatter={(value: number) => formatLocaleNumber(value, 0, i18n.language)}
            />
            <Tooltip labelFormatter={(label: string) => formatDate(label, DateFormats.DATE_WITHOUT_HOURS)} />
            <Legend />
            <Line
              type="monotone"
              dataKey="total"
              name={t('main_metrics_sku_total')}
              stroke={COLORS.BLUE}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="published"
              name={t('main_metrics_published')}
              stroke={COLORS.GREEN}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="hidden"
              name={t('main_metrics_hidden')}
              stroke={COLORS.GRAY_PRIMARY}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="deleted"
              name={t('main_metrics_deleted')}
              stroke={COLORS.RED}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </StyledChartBox>
      <StyledPanelNote>{t('main_metrics_catalog_note')}</StyledPanelNote>
    </StyledPanel>
  );
};

export const SuppliersSection = ({ suppliers }: { suppliers: ISuppliersBlock }) => {
  const { t, i18n } = useTranslation(['dashboard']);
  return (
    <StyledPanel>
      <StyledPanelTitle>{t('main_metrics_suppliers_title')}</StyledPanelTitle>
      <StyledMetricGrid3>
        <StyledMetricCard>
          <StyledMetricLabelRow>
            <StyledMetricLabel>
              <InfoLabel
                label={t('main_metrics_suppliers_total')}
                tooltip={t('main_metrics_suppliers_total_tooltip')}
              />
            </StyledMetricLabel>
            <DynamicsCell value={suppliers.total.change_percent} />
          </StyledMetricLabelRow>
          <StyledMetricValue>{formatLocaleNumber(suppliers.total.value, 0, i18n.language)}</StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabel>
            <InfoLabel
              label={t('main_metrics_suppliers_growth')}
              tooltip={t('main_metrics_suppliers_growth_tooltip')}
            />
          </StyledMetricLabel>
          <StyledMetricValue>{`+${formatLocaleNumber(suppliers.growth, 0, i18n.language)}`}</StyledMetricValue>
        </StyledMetricCard>
        <StyledMetricCard>
          <StyledMetricLabel>
            <InfoLabel
              label={t('main_metrics_suppliers_departed')}
              tooltip={t('main_metrics_suppliers_departed_tooltip')}
            />
          </StyledMetricLabel>
          <StyledMetricValue style={{ color: COLORS.RED }}>
            {formatLocaleNumber(suppliers.departed, 0, i18n.language)}
          </StyledMetricValue>
        </StyledMetricCard>
      </StyledMetricGrid3>
      <StyledPanelTitle style={{ marginTop: '1rem' }}>
        <InfoLabel
          label={t('main_metrics_suppliers_chart_title')}
          tooltip={t('main_metrics_suppliers_chart_tooltip')}
        />
      </StyledPanelTitle>
      <StyledChartBox style={{ marginTop: '1rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={suppliers.series} margin={MAIN_METRICS_CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.BRIGHT_GRAY} />
            <XAxis
              dataKey="date"
              tickFormatter={(value: string) => formatDate(value, DateFormats.DATE_WITHOUT_HOURS)}
              minTickGap={24}
            />
            <YAxis
              width={MAIN_METRICS_Y_AXIS_WIDTH}
              tickFormatter={(value: number) => formatLocaleNumber(value, 0, i18n.language)}
            />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="total"
              name={t('main_metrics_current_period')}
              stroke={COLORS.BLUE}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="prev_total"
              name={t('main_metrics_previous_period')}
              stroke={COLORS.GRAY_PRIMARY}
              strokeDasharray="5 5"
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </StyledChartBox>
    </StyledPanel>
  );
};

export const EngagementSection = ({ engagement }: { engagement: IEngagementBlock }) => {
  const { t, i18n } = useTranslation(['dashboard']);
  return (
    <StyledPanel>
      <StyledPanelTitle>
        <InfoLabel label={t('main_metrics_engagement_title')} tooltip={t('main_metrics_engagement_title_tooltip')} />
      </StyledPanelTitle>
      <StyledMetricValue>{formatPercentValue(engagement.percent, 1, i18n.language)}</StyledMetricValue>
      <DynamicsCell value={engagement.change_pp} suffix={t('cancelled_orders_pp_suffix')} />
      <StyledMetricContext style={{ marginTop: '0.5rem' }}>
        <InfoLabel label={t('main_metrics_engagement_avg')} tooltip={t('main_metrics_engagement_avg_tooltip')} />
        {`: ${formatLocaleNumber(engagement.avg_active_shops_per_day, 0, i18n.language)}`}
      </StyledMetricContext>

      <StyledPanelNote>{t('main_metrics_engagement_note')}</StyledPanelNote>
      <StyledPanelTitle style={{ marginTop: '1rem' }}>
        <InfoLabel
          label={t('main_metrics_engagement_chart_title')}
          tooltip={t('main_metrics_engagement_chart_tooltip')}
        />
      </StyledPanelTitle>
      <StyledChartBox style={{ marginTop: '1rem' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={engagement.daily} margin={MAIN_METRICS_CHART_MARGIN}>
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.BRIGHT_GRAY} />
            <XAxis
              dataKey="date"
              tickFormatter={(value: string) => formatDate(value, DateFormats.DATE_WITHOUT_HOURS)}
              minTickGap={24}
            />
            <YAxis
              width={MAIN_METRICS_Y_AXIS_WIDTH}
              tickFormatter={(value: number) => formatPercentValue(value, 0, i18n.language)}
            />
            <Tooltip
              formatter={(value: number) => formatPercentValue(value, 1, i18n.language)}
              labelFormatter={(label: string) => formatDate(label, DateFormats.DATE_WITHOUT_HOURS)}
            />
            <Bar
              dataKey="percent"
              name={t('main_metrics_engagement_daily')}
              fill={COLORS.BLUE}
              isAnimationActive={false}
            />
          </BarChart>
        </ResponsiveContainer>
      </StyledChartBox>
    </StyledPanel>
  );
};

const sortSuppliers = (rows: ITopSupplierRow[], sort: TopSortKey) => {
  const copy = [...rows];
  copy.sort((a, b) => {
    switch (sort) {
      case 'amount':
        return Number(b.amount) - Number(a.amount);
      case 'orders':
        return Number(b.orders_count) - Number(a.orders_count);
      case 'avg_check':
        return Number(b.avg_check) - Number(a.avg_check);
      default:
        return 0;
    }
  });
  return copy.map((row, index) => ({ ...row, rank: index + 1 }));
};

const sortProducts = (rows: ITopProductRow[], sort: TopSortKey) => {
  const copy = [...rows];
  copy.sort((a, b) => {
    switch (sort) {
      case 'amount':
        return Number(b.amount) - Number(a.amount);
      case 'orders':
        return Number(b.orders_count) - Number(a.orders_count);
      case 'avg_check':
        return Number(b.avg_check) - Number(a.avg_check);
      default:
        return 0;
    }
  });
  return copy.map((row, index) => ({ ...row, rank: index + 1 }));
};

export const TopSuppliersTable = ({
  suppliers,
  orderType,
  onOrderTypeChange,
}: {
  suppliers: ITopSupplierRow[];
  orderType: TopOrderType;
  onOrderTypeChange: (value: TopOrderType) => void;
}) => {
  const { t, i18n } = useTranslation(['dashboard']);
  const [sort, setSort] = useState<TopSortKey>('amount');
  const [page, setPage] = useState(1);
  const sorted = sortSuppliers(suppliers, sort);
  const pages = Math.max(1, Math.ceil(sorted.length / TOP_TABLE_PER_PAGE));
  const safePage = Math.min(page, pages);
  const pageRows = sorted.slice((safePage - 1) * TOP_TABLE_PER_PAGE, safePage * TOP_TABLE_PER_PAGE);

  return (
    <StyledPanel>
      <StyledToolbar>
        <StyledPanelTitle>
          <InfoLabel label={t('main_metrics_top_suppliers')} tooltip={t('main_metrics_top_suppliers_tooltip')} />
        </StyledPanelTitle>
        <StyledToggleGroup>
          <Select
            value={orderType}
            style={{ minWidth: 200 }}
            onChange={(value) => onOrderTypeChange(value as TopOrderType)}
            options={(['active', 'completed'] as TopOrderType[]).map((key) => ({
              value: key,
              label: t(`main_metrics_order_type_${key}`),
            }))}
          />
          <Select
            value={sort}
            style={{ minWidth: 180 }}
            onChange={(value) => setSort(value as TopSortKey)}
            options={(['amount', 'orders', 'avg_check'] as TopSortKey[]).map((key) => ({
              value: key,
              label: t(`main_metrics_sort_${key}`),
            }))}
          />
          <ExportCsvButton
            onClick={() =>
              downloadCsv(
                'top-suppliers.csv',
                [
                  '#',
                  t('main_metrics_supplier'),
                  t('main_metrics_amount'),
                  t('main_metrics_orders'),
                  t('main_metrics_avg_check'),
                  t('main_metrics_dynamics'),
                ],
                sorted.map((row) => [
                  row.rank,
                  row.name,
                  row.amount,
                  row.orders_count,
                  row.avg_check,
                  row.change_percent ?? '',
                ]),
              )
            }
          />
        </StyledToggleGroup>
      </StyledToolbar>
      <Table<ITopSupplierRow>
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={pageRows}
        columns={[
          { title: '#', key: 'rank', dataIndex: 'rank', width: 32, align: 'center' as const },
          { title: t('main_metrics_supplier'), key: 'name', dataIndex: 'name' },
          {
            title: (
              <InfoLabel label={t('main_metrics_amount')} tooltip={t('main_metrics_top_suppliers_amount_tooltip')} />
            ),
            key: 'amount',
            render: (_, row) => formatMoneyWithSeparators(row.amount),
          },
          {
            title: (
              <InfoLabel label={t('main_metrics_orders')} tooltip={t('main_metrics_top_suppliers_orders_tooltip')} />
            ),
            key: 'orders',
            render: (_, row) => formatLocaleNumber(row.orders_count, 0, i18n.language),
          },
          {
            title: (
              <InfoLabel
                label={t('main_metrics_avg_check')}
                tooltip={t('main_metrics_top_suppliers_avg_check_tooltip')}
              />
            ),
            key: 'avg_check',
            render: (_, row) => formatMoneyWithSeparators(row.avg_check),
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
          },
          {
            title: (
              <InfoLabel
                label={t('main_metrics_dynamics')}
                tooltip={t('main_metrics_top_suppliers_dynamics_tooltip')}
              />
            ),
            key: 'dynamics',
            render: (_, row) => <DynamicsCell value={row.change_percent} />,
          },
        ]}
      />
      <TablePager page={safePage} pages={pages} onChange={setPage} />
    </StyledPanel>
  );
};

export const TopProductsTable = ({
  products,
  orderType,
  onOrderTypeChange,
}: {
  products: ITopProductRow[];
  orderType: TopOrderType;
  onOrderTypeChange: (value: TopOrderType) => void;
}) => {
  const { t, i18n } = useTranslation(['dashboard']);
  const [sort, setSort] = useState<TopSortKey>('amount');
  const [page, setPage] = useState(1);
  const sorted = sortProducts(products, sort);
  const pages = Math.max(1, Math.ceil(sorted.length / TOP_TABLE_PER_PAGE));
  const safePage = Math.min(page, pages);
  const pageRows = sorted.slice((safePage - 1) * TOP_TABLE_PER_PAGE, safePage * TOP_TABLE_PER_PAGE);

  return (
    <StyledPanel>
      <StyledToolbar>
        <StyledPanelTitle>
          <InfoLabel label={t('main_metrics_top_products')} tooltip={t('main_metrics_top_products_tooltip')} />
        </StyledPanelTitle>
        <StyledToggleGroup>
          <Select
            value={orderType}
            style={{ minWidth: 200 }}
            onChange={(value) => onOrderTypeChange(value as TopOrderType)}
            options={(['active', 'completed'] as TopOrderType[]).map((key) => ({
              value: key,
              label: t(`main_metrics_order_type_${key}`),
            }))}
          />
          <Select
            value={sort}
            style={{ minWidth: 180 }}
            onChange={(value) => setSort(value as TopSortKey)}
            options={(['amount', 'orders', 'avg_check'] as TopSortKey[]).map((key) => ({
              value: key,
              label: t(`main_metrics_sort_${key}`),
            }))}
          />
          <ExportCsvButton
            onClick={() =>
              downloadCsv(
                'top-products.csv',
                [
                  '#',
                  t('main_metrics_product'),
                  'SKU',
                  t('main_metrics_supplier'),
                  t('main_metrics_amount'),
                  t('main_metrics_orders'),
                  t('main_metrics_avg_check'),
                  t('main_metrics_dynamics'),
                ],
                sorted.map((row) => [
                  row.rank,
                  row.name,
                  row.sku,
                  row.supplier_name,
                  row.amount,
                  row.orders_count,
                  row.avg_check,
                  row.change_percent ?? '',
                ]),
              )
            }
          />
        </StyledToggleGroup>
      </StyledToolbar>
      <Table<ITopProductRow>
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={pageRows}
        columns={[
          { title: '#', key: 'rank', dataIndex: 'rank', width: 32, align: 'center' as const },
          { title: t('main_metrics_product'), key: 'name', dataIndex: 'name' },
          { title: 'SKU', key: 'sku', dataIndex: 'sku' },
          { title: t('main_metrics_supplier'), key: 'supplier', dataIndex: 'supplier_name' },
          {
            title: (
              <InfoLabel label={t('main_metrics_amount')} tooltip={t('main_metrics_top_products_amount_tooltip')} />
            ),
            key: 'amount',
            render: (_, row) => formatMoneyWithSeparators(row.amount),
          },
          {
            title: (
              <InfoLabel label={t('main_metrics_orders')} tooltip={t('main_metrics_top_products_orders_tooltip')} />
            ),
            key: 'orders',
            render: (_, row) => formatLocaleNumber(row.orders_count, 0, i18n.language),
          },
          {
            title: (
              <InfoLabel
                label={t('main_metrics_avg_check')}
                tooltip={t('main_metrics_top_products_avg_check_tooltip')}
              />
            ),
            key: 'avg_check',
            render: (_, row) => formatMoneyWithSeparators(row.avg_check),
            onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
          },
          {
            title: (
              <InfoLabel label={t('main_metrics_dynamics')} tooltip={t('main_metrics_top_products_dynamics_tooltip')} />
            ),
            key: 'dynamics',
            render: (_, row) => <DynamicsCell value={row.change_percent} />,
          },
        ]}
      />
      <TablePager page={safePage} pages={pages} onChange={setPage} />
    </StyledPanel>
  );
};

export type { IAdminMainMetricsData };
